import { useState, useEffect } from 'react';
import { getDocumentPreviewUrl } from '../lib/api';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { 
  X, 
  FileText, 
  Image as ImageIcon, 
  FileType,
  ZoomIn,
  ZoomOut,
  Download,
  Loader2
} from 'lucide-react';

export default function DocumentPreview({ caseId, document, onClose }) {
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(100);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);

  const token = localStorage.getItem('token');
  // Use token as query param for browser elements that can't send headers
  const previewUrl = `${getDocumentPreviewUrl(caseId, document.id)}?token=${token}`;

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50));

  const handleDownload = async () => {
    try {
      // Use the preview URL which already has the token
      const response = await fetch(previewUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const getFileIcon = () => {
    switch (document.file_type) {
      case 'pdf': return <FileText className="w-6 h-6 text-red-500" />;
      case 'docx': return <FileType className="w-6 h-6 text-blue-500" />;
      case 'image': return <ImageIcon className="w-6 h-6 text-green-500" />;
      default: return <FileText className="w-6 h-6 text-muted-foreground" />;
    }
  };

  const renderPreview = () => {
    if (document.file_type === 'image') {
      return (
        <div className="flex items-center justify-center h-full bg-muted/30 p-4">
          <img
            src={previewUrl}
            alt={document.filename}
            style={{ transform: `scale(${zoom / 100})`, transition: 'transform 0.2s' }}
            className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError('Failed to load image');
            }}
          />
        </div>
      );
    }

    if (document.file_type === 'pdf') {
      // Try multiple approaches for PDF display
      return (
        <div className="w-full h-full flex flex-col">
          {/* Primary: Embedded PDF viewer */}
          <embed
            src={previewUrl}
            type="application/pdf"
            className="w-full flex-1 border-0"
            title={document.filename}
          />
          {/* Fallback section at bottom */}
          <div className="p-4 bg-muted/30 border-t text-center">
            <p className="text-sm text-muted-foreground mb-2">
              Can't see the PDF? 
            </p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => window.open(previewUrl, '_blank')}
              >
                Open in New Tab
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // For DOCX and other text files, show extracted content
    return (
      <ScrollArea className="h-full">
        <div className="p-6 bg-white dark:bg-card">
          <div className="prose dark:prose-invert max-w-none">
            <h3 className="font-heading text-lg mb-4">{document.filename}</h3>
            <div className="font-body text-sm whitespace-pre-wrap leading-relaxed">
              {document.content || document.content_preview || 'No preview available for this document type.'}
            </div>
          </div>
        </div>
      </ScrollArea>
    );
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  // Set loading to false after a short delay for PDFs (object tag doesn't have reliable onLoad)
  useEffect(() => {
    if (document.file_type === 'pdf') {
      const timer = setTimeout(() => setLoading(false), 1000);
      return () => clearTimeout(timer);
    }
    if (document.file_type !== 'image') {
      setLoading(false);
    }
  }, [document.file_type]);

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm" data-testid="document-preview">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          {getFileIcon()}
          <div>
            <h2 className="font-body font-semibold text-foreground">{document.filename}</h2>
            <p className="font-mono text-xs text-muted-foreground">
              {document.file_type.toUpperCase()} • {(document.file_size / 1024).toFixed(1)} KB
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(document.file_type === 'image' || document.file_type === 'pdf') && (
            <>
              <Button variant="outline" size="icon" onClick={handleZoomOut} disabled={zoom <= 50}>
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="font-mono text-sm w-12 text-center">{zoom}%</span>
              <Button variant="outline" size="icon" onClick={handleZoomIn} disabled={zoom >= 200}>
                <ZoomIn className="w-4 h-4" />
              </Button>
            </>
          )}
          <Button variant="outline" size="icon" onClick={handleDownload} data-testid="download-doc-btn">
            <Download className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-preview-btn">
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Preview Area */}
      <div className="h-[calc(100vh-73px)] relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10">
            <Loader2 className="w-8 h-8 animate-spin text-bronze" />
          </div>
        )}
        {error ? (
          <div className="flex flex-col items-center justify-center h-full">
            <FileText className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <p className="font-body text-muted-foreground">{error}</p>
            <Button variant="outline" onClick={handleDownload} className="mt-4">
              <Download className="w-4 h-4 mr-2" />
              Download Instead
            </Button>
          </div>
        ) : (
          renderPreview()
        )}
      </div>
    </div>
  );
}
